import { isEmpty } from "lodash";
import { eventChannel } from "redux-saga";
import {
  all,
  call,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";

import {
  appUpdateComplete,
  AuctionPenAction,
  BidderRegistrationAction,
  BillingTagsAction,
  BusinessAction,
  CheckpointAction,
  clearNvdUploads,
  clearP2PFiles,
  clearSellFiles,
  clearSellReversals,
  clearTakeFiles,
  clearTakeReversals,
  DeploymentAction,
  DraftingInformationAction,
  EnvdDocumentAction,
  fetchGeoData,
  fetchLivestockSaleDataChanges,
  IntegrationBusinessAction,
  ManualAdjustmentAction,
  NominationAction,
  PaymentAction,
  PenArchetypeAction,
  pingFromApp,
  requestAgencies,
  requestAllSellFiles,
  requestAllSellReversals,
  requestAllTakeReversals,
  requestAllTakeStatuses,
  requestAttachments,
  requestConsignments,
  requestDefaultRoundsAndSaleTypes,
  requestDeploymentAttributes,
  requestProducts,
  requestProperties,
  requestScans,
  requestSpeciesAttributes,
  SaleAction,
  SaleLotAction,
  SaleyardAction,
  SavedViewAction,
  ServerHeartbeatAction,
  setSetting,
  SingleWeighAction,
  updateRefreshInterval,
} from "actions";

import { BillingSettingsAction } from "actions/billingSettings";
import { BusinessLabelAction } from "actions/businessLabel";
import { BusinessRelationAction } from "actions/businessRelation";
import { SaleBuyerSplitAction } from "actions/buyerSplits";
import { CandidateDeclarationAction } from "actions/candidateDeclaration";
import { EContractAction } from "actions/eContracts";
import { EContractAgreementAction } from "actions/eContractAgreements";
import { EContractSpecialConditionAction } from "actions/eContractSpecialConditions";
import { DressingRangesAction } from "actions/DressingRanges";
import { IntegrationCredentialAction } from "actions/integrationCredentials";
import { LedgerAccountAction } from "actions/ledgerAccounts";
import { LivestockAgentAction } from "actions/livestockAgents";
import { MasterLabelAction } from "actions/masterLabels";
import { MasterLedgerAccountAction } from "actions/masterLedgerAccounts";
import { NominationTermsAction } from "actions/nominationTerms";
import { SaleStatusAction } from "actions/saleStatus";
import { SaleSubTypeAction } from "actions/saleSubTypes";
import { VendorCommissionBandAction } from "actions/vendorCommissionBands";
import { SaleVendorSplitAction } from "actions/vendorSplits";
import { WeightRangesAction } from "actions/weightRanges";

import {
  API_RESPONSE,
  DEPLOYMENT,
  HUB_CONNECTED,
  HUB_DISCONNECTED,
  REFRESH_INTERVAL,
  REHYDRATE_COMPLETE,
  REQUEST_LIVESTOCK_SALE_DATA,
  REQUEST_SYSTEM_DATA,
  REQUEST_USER_ROLE_DATA,
  SALE,
  SALEYARD,
  SET_ACTIVE_ROLE,
  SET_CURRENT_LIVESTOCKSALE,
  SIMULATE_REDUX_SAGA_ERROR,
} from "constants/actionTypes";
import {
  DeploymentPermissions,
  SaleyardPermissions,
} from "constants/permissions";
import { TransactionalSaleTypes } from "constants/sale";
import { Settings } from "constants/settings";
import { effectMap } from "constants/system";

import {
  actionFilters,
  deploymentPermissionCheckActionTypes,
  inSalePermissionCheckActionTypes,
  saleyardAndDeploymentPermissionCheckActionTypes,
} from "lib/actionFilters";
import { clearHeartBeatInterval, heartBeatTrigger } from "lib/heartBeat";
import {
  getLivestockSaleId,
  getSaleyardName,
  openSale,
  redirectToFirstSaleyardFromRole,
  redirectToLoads,
} from "lib/navigation";
import { hasPermission } from "lib/permissions";
import { getApiSale } from "lib/sale";

import {
  getActiveLivestockAgentDeployment,
  getActiveRole,
  getCurrentSale,
  getIsBusinessUser,
  getIsDefaultRoleTransporter,
  getIsHubConnected,
  getIsLivestockAgent,
  getIsSaleyardAdmin,
  getIsScaleOperator,
  getIsTransporter,
  getRefreshIntervalMs,
  getSetting,
  selectCurrentSaleyard,
} from "selectors";

function* onSimulateReduxSagaError() {
  yield;

  throw new Error("Simulated Redux Saga Error");
}

/**
 * Processes and triggers events related to when a API GET request succeeds
 * @param {{ type: string, response: Response }} action
 */
function* onApiResponseGetSuccess(action) {
  const { response } = action;
  // Process any potential changes to the heartbeat interval (only if the server has indicated that it has a preference for the request)
  // The changes since APIs should respond with a refresh interval header.
  const refreshIntervalRaw = response.headers.get("x-refresh-interval");
  if (refreshIntervalRaw) {
    const refreshIntervalMs = parseInt(refreshIntervalRaw, 10);
    const state = yield select();
    const currentRefreshIntervalMs = getRefreshIntervalMs(state);

    if (currentRefreshIntervalMs !== refreshIntervalMs) {
      yield put(updateRefreshInterval(refreshIntervalMs));
    }
  }
}

function* onApiResponseGetUpdateFailure(action) {
  const { error, action: originalAction } = action;
  const { response } = error || {};

  // TODO - document this - what are we trying to do with it?
  // The salelot number can come from two different spots, so check both and if not there return blank
  const effectType = effectMap(
    originalAction?.payload?.saleLot?.lot_number || response?.lot_number || "",
  )[originalAction.type];
  if (effectType) {
    try {
      if (error.status === 404) {
        yield put(effectType.delete(action?.meta?.offline?.commit?.meta?.id));
      }
      if (effectType && (error.status === 409 || error.status === 400)) {
        yield call(effectType.fetch, action.meta);
      }
      if (error.method === "DELETE") {
        yield call(effectType.fetch, action?.meta?.offline?.commit?.meta?.id);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  }
}

function* onRequestSaleData() {
  const livestockSaleId = getLivestockSaleId();

  const apiSale = getApiSale();

  const state = yield select();
  const isBusinessUser = getIsBusinessUser(state);
  const isScaleOperator = getIsScaleOperator(state);
  const isSaleyardAdmin = getIsSaleyardAdmin(state);
  const isLivestockAgent = getIsLivestockAgent(state);
  const saleyard = selectCurrentSaleyard(state);
  const deployment = getActiveLivestockAgentDeployment(state);
  const sale = getCurrentSale(state);

  const hasDeploymentVendorSplitPermission = hasPermission(
    deployment,
    DeploymentPermissions.featureVendorSplits,
  );
  const hasSaleyardVendorSplitPermission = hasPermission(
    saleyard,
    SaleyardPermissions.featureVendorSplits,
  );
  const hasVendorSplitPermission =
    hasDeploymentVendorSplitPermission || hasSaleyardVendorSplitPermission;

  const hasDeploymentBuyerSplitPermission = hasPermission(
    deployment,
    DeploymentPermissions.featureBuyerSplits,
  );

  const hasSaleyardBuyerSplitPermission = hasPermission(
    saleyard,
    SaleyardPermissions.featureBuyerSplits,
  );
  const hasBuyerSplitPermission =
    hasDeploymentBuyerSplitPermission || hasSaleyardBuyerSplitPermission;

  // if we got here without a saleyard, or get here as a livestock agent with no deployment
  //( eg, this can happen from a king link ) then these calls will have nothing to check permissions against.
  // The successful receipt of the get deployments/get saleyard will trigger
  // the inSalePermissionCheckActionTypes as well.
  if (!isEmpty(saleyard) && !(isLivestockAgent && isEmpty(deployment))) {
    // Otherwise, fire these off - they should check permissions fine.
    yield all(
      inSalePermissionCheckActionTypes
        .map(actionSet => actionFilters[actionSet.dataType](state, false))
        .filter(Boolean)
        .map(action => put(action)),
    );
  }

  // If the sale has changed, get the sale dependent data.
  if (!isBusinessUser) {
    // There's a bunch of data a business doesn't need - don't get it!
    if (sale?.using_registered_bidders) {
      yield put(BidderRegistrationAction.request());
    }
    yield put(requestAttachments(apiSale));
  }

  if (isScaleOperator) {
    yield put(requestAllTakeStatuses(apiSale));
    yield put(requestAllTakeReversals(apiSale));

    yield put(requestAllSellReversals(apiSale));
    yield put(requestAllSellFiles(apiSale));
  }

  if (isSaleyardAdmin || isLivestockAgent) {
    yield put(clearTakeFiles(apiSale));
    yield put(clearTakeReversals(apiSale));
    yield put(clearNvdUploads(apiSale));
    yield put(clearSellFiles(apiSale));
    yield put(clearSellReversals(apiSale));
    yield put(clearP2PFiles(apiSale));

    // For ENVDs and OCR results
    yield put(CandidateDeclarationAction.request({ livestockSaleId }));
    yield put(EnvdDocumentAction.request({ livestockSaleId }));
  }

  yield put(CheckpointAction.request({ livestockSaleId }));
  yield put(SaleLotAction.request());
  yield put(requestScans(apiSale));
  yield put(AuctionPenAction.request());
  yield put(requestConsignments(apiSale));
  yield put(ManualAdjustmentAction.request());
  yield put(NominationAction.request({ livestockSaleId }));
  yield put(
    PaymentAction.request({
      livestockSalesIdIn: [livestockSaleId],
    }),
  );
  yield put(PenArchetypeAction.request());
  yield put(SingleWeighAction.request());

  if (hasVendorSplitPermission) {
    yield put(SaleVendorSplitAction.request({ livestockSaleId }));
  }

  if (hasBuyerSplitPermission) {
    yield put(SaleBuyerSplitAction.request({ livestockSaleId }));
  }

  if (sale && TransactionalSaleTypes.includes(sale.sale_type)) {
    yield put(EContractAgreementAction.request());
    yield put(EContractAction.request());
    yield put(EContractSpecialConditionAction.request());
  }
}

function* onRequestUserRoleData() {
  const state = yield select();
  const activeRole = getActiveRole(state);
  const isBusinessUser = getIsBusinessUser(state);
  const isLivestockAgent = getIsLivestockAgent(state);
  const isTransporter = getIsTransporter(state);
  const saleyardName = activeRole.saleyards?.[0].name;
  if (activeRole.slug) {
    // always get geo data and server settings (heartbeat response)
    yield put(fetchGeoData());
    yield put(ServerHeartbeatAction.request());

    // Everyone wants saved views
    yield put(SavedViewAction.request());

    // Transporters have a narrower set of relevant user role data.
    // The only piece of data we need to get for transporters is SavedViews (and loads, but they're dispatched
    // from the loads UI on a timer)
    if (!isTransporter) {
      // TODO: Refactor the businesses endpoint to just use the role (For GET)
      yield put(SaleAction.request());
      if (saleyardName && !isBusinessUser) {
        yield put(BusinessAction.request());
      }

      yield put(SaleStatusAction.request());

      // Load the deployment's agents up front so Manager / Assignee chips
      // resolve names from the directory (feature gating happens in the UI).
      if (isLivestockAgent) {
        yield put(LivestockAgentAction.request());
      }

      yield put(requestDefaultRoundsAndSaleTypes());
      yield put(DraftingInformationAction.request());

      yield put(requestAgencies());
      yield put(DeploymentAction.request());
      yield put(requestDeploymentAttributes());
      yield put(MasterLabelAction.request());
      yield put(BusinessLabelAction.request());
      yield put(requestProducts());
      yield put(SaleyardAction.request());
      yield put(NominationTermsAction.request());
      yield put(requestProperties());
      yield put(VendorCommissionBandAction.request());
      yield put(IntegrationCredentialAction.request());
      yield put(IntegrationBusinessAction.request());

      yield put(LedgerAccountAction.request());
      yield put(MasterLedgerAccountAction.request());
      yield put(BusinessRelationAction.request());
      yield put(BillingSettingsAction.request());
    }
  }
}

function* onRequestDeploymentPermissionData() {
  const state = yield select();
  const isLivestockAgent = getIsLivestockAgent(state);

  if (isLivestockAgent) {
    yield all(
      deploymentPermissionCheckActionTypes
        .map(actionSet => actionFilters[actionSet.dataType](state, false))
        .filter(Boolean)
        .map(action => put(action)),
    );
  }
  yield all(
    saleyardAndDeploymentPermissionCheckActionTypes
      .map(actionSet => actionFilters[actionSet.dataType](state, false))
      .filter(Boolean)
      .map(action => put(action)),
  );

  // If we're in a sale and the deployment data has just landed.
  // Consider firing off the inSalePermissionCheckActionTypes - we may have skipped one
  // of the actions if the deployment was unavailable to offer permisisons.
  // Duplicate or queued actions should be cancelled out anyway.
  if (getLivestockSaleId()) {
    yield all(
      inSalePermissionCheckActionTypes
        .map(actionSet => actionFilters[actionSet.dataType](state, false))
        .filter(Boolean)
        .map(action => put(action)),
    );
  }
}

function* onRequestSaleyardPermissionData() {
  const state = yield select();
  yield all(
    saleyardAndDeploymentPermissionCheckActionTypes
      .map(actionSet => actionFilters[actionSet.dataType](state, false))
      .filter(Boolean)
      .map(action => put(action)),
  );
  // If we're in a sale and the saleyard data has just landed.
  // Consider firing off the inSalePermissionCheckActionTypes - we may have skipped one
  // of the actions if the saleyard was unavailable to offer permisisons.
  // Duplicate or queued actions should be cancelled out anyway.
  if (getLivestockSaleId()) {
    yield all(
      inSalePermissionCheckActionTypes
        .map(actionSet => actionFilters[actionSet.dataType](state, false))
        .filter(Boolean)
        .map(action => put(action)),
    );
  }
}

function* onRequestSystemData() {
  // ages
  // breeds
  // categories
  // grades
  // exemptions
  // labels
  // nomination terms
  // rounds
  // sexes
  // species

  // Transporters don't need this data (yet?)
  // We might be landing here before we've fully pulled down transporters, though, so check the default role if that's the case.
  const state = yield select();
  const requestSaleSystemData = getActiveRole(state)?.id
    ? getIsTransporter(state)
    : getIsDefaultRoleTransporter(state);
  if (!requestSaleSystemData) {
    yield put(requestSpeciesAttributes());
    // sale sub types
    yield put(SaleSubTypeAction.request());
    yield put(WeightRangesAction.request());
    yield put(DressingRangesAction.request());
    yield put(BillingTagsAction.request());
  }
}

function* onRehydrateComplete() {
  const state = yield select();
  if (!isEmpty(getActiveRole(state))) {
    // Refresh the system data on refresh/rehydrate if we have a role.
    yield put({ type: REQUEST_SYSTEM_DATA });
  }

  // We've refreshed so clear any app update status
  yield put(appUpdateComplete());
}

function* onSetActiveRoleSuccess(action) {
  const { hasRoleChanged } = action;
  // Request role specific
  if (hasRoleChanged) {
    const state = yield select();
    const { auth } = state;
    const currentSale = getCurrentSale(state);
    const isTransporter = getIsTransporter(state);
    const currentSaleYardName = getSaleyardName();

    if (isTransporter) {
      // When we change role to a transporter, send them off to the loads page.
      redirectToLoads();
    } else if (
      // If we know that the new role doesn't have access to the saleyard, boot them to one they do have access to.
      auth?.activeRole?.saleyards?.length > 0 &&
      currentSaleYardName &&
      !auth.activeRole.saleyards.map(s => s.name).includes(currentSaleYardName)
    ) {
      redirectToFirstSaleyardFromRole(auth);
    } else if (currentSale) {
      // When changing role while in a sale, re-open to default tab/url for the new role
      openSale(currentSale);
    }

    yield put({ type: REQUEST_USER_ROLE_DATA });
  }
}

function* onSetCurrentLivestockSaleSuccess({
  livestockSaleId,
  requestLivestockSaleData,
}) {
  if (livestockSaleId && requestLivestockSaleData) {
    yield put({ type: REQUEST_LIVESTOCK_SALE_DATA });
  }
}

function* heartBeatInterval() {
  clearHeartBeatInterval();

  const state = yield select();

  const refreshIntervalMs = getRefreshIntervalMs(state);
  const isHubConnected = getIsHubConnected(state);
  const firstTick = Date.now() + refreshIntervalMs;
  const channel = eventChannel(emit => {
    heartBeatTrigger(
      () => {
        getLivestockSaleId()
          ? emit(fetchLivestockSaleDataChanges(getApiSale(), getSaleyardName()))
          : emit(ServerHeartbeatAction.request());
      },
      () => {
        if (isHubConnected) {
          emit(pingFromApp());
        }
      },
      refreshIntervalMs,
      firstTick,
      Date.now(),
    );
    return () => {
      clearHeartBeatInterval();
    };
  });
  try {
    while (true) {
      const action = yield take(channel);
      yield put(action);
    }
  } finally {
    clearHeartBeatInterval();
  }
}

function* handleGetFailure(action) {
  const { isCatastrophic } = action;
  if (isCatastrophic) {
    // Failure to get this request means stuff pretty broken (eg a sale with no lots) so tell them
    const state = yield select();
    const dismissedUntil = getSetting(
      Settings.failedRequestModalDismissedUntil,
    )(state);
    if (!dismissedUntil || dismissedUntil < Date.now()) {
      yield put(setSetting(Settings.isFailedRequestsModalShowing, true));
    }
  }
}

export default function* systemSaga() {
  yield takeLatest(REQUEST_LIVESTOCK_SALE_DATA, onRequestSaleData);
  yield takeLatest(REQUEST_USER_ROLE_DATA, onRequestUserRoleData);
  yield takeLatest(REQUEST_SYSTEM_DATA, onRequestSystemData);
  yield takeEvery(
    DEPLOYMENT.FETCH_BULK.SUCCESS,
    onRequestDeploymentPermissionData,
  );
  yield takeEvery(SALEYARD.FETCH_BULK.SUCCESS, onRequestSaleyardPermissionData);

  yield takeEvery(API_RESPONSE.FETCH.SUCCESS, onApiResponseGetSuccess);
  yield takeEvery(API_RESPONSE.UPDATE.FAILURE, onApiResponseGetUpdateFailure);
  yield takeEvery(SIMULATE_REDUX_SAGA_ERROR, onSimulateReduxSagaError);

  yield takeLatest(REHYDRATE_COMPLETE, onRehydrateComplete);

  yield takeLatest(
    [
      SALE.FETCH_BULK.SUCCESS,
      SET_CURRENT_LIVESTOCKSALE.SUCCESS,
      REFRESH_INTERVAL.ACTION,
      HUB_CONNECTED,
      HUB_DISCONNECTED,
    ],
    heartBeatInterval,
  );

  yield takeLatest(
    SET_CURRENT_LIVESTOCKSALE.SUCCESS,
    onSetCurrentLivestockSaleSuccess,
  );
  yield takeLatest(SET_ACTIVE_ROLE.SUCCESS, onSetActiveRoleSuccess);
  yield takeLatest(SET_ACTIVE_ROLE.RESET, onSetActiveRoleSuccess);
  yield takeEvery(API_RESPONSE.FETCH.FAILURE, handleGetFailure);
}
